/*
 * Pure's shared single-select enhancement. The native select remains Shopify's
 * source of truth, in its original parent, with all form and option metadata.
 * Keyboard behavior follows the WAI-ARIA select-only combobox pattern.
 */
(() => {
  'use strict';

  if (window.FMPureDropdowns) {
    window.FMPureDropdowns.refresh();
    return;
  }

  const instances = new WeakMap();
  const states = new Set();
  const hosts = new Map();
  const pending = new Set();
  let serial = 0;
  let opened = null;
  let flushQueued = false;
  let escapeKeyUp = false;
  let escapeTimer;
  let invalidFocusClaimed = false;

  const eligible = (select) => select instanceof HTMLSelectElement && !select.multiple && select.size <= 1;
  const attr = (node, name, value) => {
    if (value === null || value === undefined || value === '') {
      if (node.hasAttribute(name)) node.removeAttribute(name);
    } else if (node.getAttribute(name) !== String(value)) {
      node.setAttribute(name, String(value));
    }
  };
  const text = (node, value) => {
    if (node.textContent !== value) node.textContent = value;
  };
  const element = (tag, className) => {
    const node = document.createElement(tag);
    node.className = className;
    return node;
  };
  const on = (state, target, event, listener, options) => {
    target.addEventListener(event, listener, options);
    state.listeners.push(() => target.removeEventListener(event, listener, options));
  };

  function ownHost(state) {
    const host = state.select.parentElement;
    if (host === state.host) return;
    releaseHost(state);
    if (!host) return;
    const record = hosts.get(host) || { count: 0, original: host.getAttribute('data-fm-dropdown-host') };
    record.count += 1;
    hosts.set(host, record);
    host.setAttribute('data-fm-dropdown-host', '');
    state.host = host;
  }

  function releaseHost(state) {
    const record = hosts.get(state.host);
    if (!record) return;
    record.count -= 1;
    if (!record.count) {
      if (record.original === null) state.host.removeAttribute('data-fm-dropdown-host');
      else state.host.setAttribute('data-fm-dropdown-host', record.original);
      hosts.delete(state.host);
    }
    state.host = null;
  }

  function labelsFor(select) {
    return Array.from(select.labels || []);
  }

  function labelText(select) {
    return labelsFor(select).map((label) => {
      const copy = label.cloneNode(true);
      copy.querySelectorAll('select, input, button, .fm-dropdown').forEach((node) => node.remove());
      return copy.textContent.trim();
    }).filter(Boolean).join(' ');
  }

  function syncName(state) {
    const { select, trigger, menu } = state;
    const labelledBy = select.getAttribute('aria-labelledby');
    const name = select.getAttribute('aria-label') || labelText(select) || select.name || 'Choose an option';
    attr(trigger, 'aria-labelledby', labelledBy);
    attr(trigger, 'aria-label', labelledBy ? null : name);
    attr(menu, 'aria-label', name);
    attr(trigger, 'data-fm-dropdown-select', select.id);
    attr(trigger, 'title', select.getAttribute('title'));
    attr(trigger, 'aria-required', select.required ? 'true' : null);
    // Keep the native tab-order intent, including an intentionally excluded field.
    attr(trigger, 'tabindex', state.original.tabindex);
  }

  function optionData(select) {
    return Array.from(select.options).map((option, index) => {
      const group = option.parentElement?.tagName === 'OPTGROUP' ? option.parentElement : null;
      return {
        option,
        index,
        label: option.label || option.textContent || '',
        group,
        hidden: option.hidden || Boolean(group?.hidden),
        disabled: option.disabled || Boolean(group?.disabled),
      };
    });
  }

  function rebuildOptions(state, options) {
    const fragment = document.createDocumentFragment();
    const groups = new Map();
    state.items = [];
    options.forEach((item) => {
      if (item.hidden) return;
      let parent = fragment;
      if (item.group) {
        if (!groups.has(item.group)) {
          const group = element('div', 'fm-dropdown__group');
          const label = element('div', 'fm-dropdown__group-label');
          label.id = `${state.id}-group-${groups.size}`;
          label.textContent = item.group.label;
          group.setAttribute('role', 'group');
          group.setAttribute('aria-labelledby', label.id);
          group.append(label);
          groups.set(item.group, group);
          fragment.append(group);
        }
        parent = groups.get(item.group);
      }
      const node = element('div', 'fm-dropdown__option');
      node.id = `${state.id}-option-${item.index}`;
      node.dataset.index = String(item.index);
      node.setAttribute('role', 'option');
      node.setAttribute('aria-selected', String(item.index === state.select.selectedIndex));
      if (item.disabled) node.setAttribute('aria-disabled', 'true');
      node.textContent = item.label;
      parent.append(node);
      state.items.push({ ...item, node });
    });
    if (!state.items.length) {
      const empty = element('div', 'fm-dropdown__empty');
      empty.textContent = 'No options available';
      fragment.append(empty);
    }
    state.menu.replaceChildren(fragment);
  }

  function syncError(state) {
    if (state.select.validity.valid) state.invalid = false;
    const message = state.invalid ? state.select.validationMessage : '';
    state.error.hidden = !message;
    text(state.error, message);
    attr(state.trigger, 'aria-invalid', message ? 'true' : state.select.getAttribute('aria-invalid'));
    const describedBy = [state.select.getAttribute('aria-describedby'), message ? state.error.id : '']
      .filter(Boolean).join(' ');
    attr(state.trigger, 'aria-describedby', describedBy);
  }

  function sync(state, resetActive = false) {
    if (!state.select.isConnected || !eligible(state.select)) {
      destroy(state);
      return;
    }
    ownHost(state);
    if (state.select.nextElementSibling !== state.wrapper) state.select.after(state.wrapper);
    state.wrapper.hidden = state.select.hidden;
    syncName(state);
    const options = optionData(state.select);
    const signature = JSON.stringify(options.map(({ index, label, group, hidden, disabled, option }) =>
      [index, label, group?.label, group ? Array.from(state.select.children).indexOf(group) : -1, hidden, disabled, option.value]));
    const rebuilt = signature !== state.signature;
    if (rebuilt) {
      rebuildOptions(state, options);
      state.signature = signature;
    }
    state.trigger.disabled = state.select.matches(':disabled') || !state.items.some((item) => !item.disabled);
    text(state.value, state.items.length ? state.select.selectedOptions[0]?.label || 'Choose an option' : 'No options available');
    state.items.forEach((item) => attr(item.node, 'aria-selected', String(item.index === state.select.selectedIndex)));
    syncError(state);
    if (opened === state) {
      if (state.trigger.disabled || state.wrapper.hidden) {
        close(state);
      } else {
        if (resetActive || rebuilt || !enabled(state).some((item) => item.index === state.active)) {
          state.active = initialActive(state);
        }
        setActive(state, state.active);
        if (rebuilt) place(state);
      }
    }
  }

  function enabled(state) {
    return state.items.filter((item) => !item.disabled);
  }

  function initialActive(state) {
    const items = enabled(state);
    return items.some((item) => item.index === state.select.selectedIndex)
      ? state.select.selectedIndex : (items[0]?.index ?? -1);
  }

  function setActive(state, index, ensureVisible = true) {
    state.active = index;
    let active;
    state.items.forEach((item) => {
      const isActive = opened === state && item.index === index;
      attr(item.node, 'data-active', isActive ? 'true' : null);
      if (isActive) active = item.node;
    });
    attr(state.trigger, 'aria-activedescendant', active?.id);
    if (!active || !ensureVisible) return;
    // Scroll the popup only. scrollIntoView would also move the page/drawer.
    const optionRect = active.getBoundingClientRect();
    const menuRect = state.menu.getBoundingClientRect();
    if (optionRect.top < menuRect.top + 6) state.menu.scrollTop -= menuRect.top + 6 - optionRect.top;
    else if (optionRect.bottom > menuRect.bottom - 6) state.menu.scrollTop += optionRect.bottom - menuRect.bottom + 6;
  }

  function fallbackParent(state) {
    // Stay inside a dialog/inert boundary so its focus trap still owns this menu.
    return state.select.closest('dialog[open], [aria-modal="true"], [role="dialog"], menu-drawer, .menu-drawer-container[open]')
      || document.body;
  }

  function place(state) {
    if (opened !== state) return;
    const rect = state.trigger.getBoundingClientRect();
    const viewport = window.visualViewport;
    const bounds = {
      left: viewport?.offsetLeft || 0,
      top: viewport?.offsetTop || 0,
      width: viewport?.width || document.documentElement.clientWidth,
      height: viewport?.height || window.innerHeight,
    };
    const margin = 8;
    const gap = 6;
    const below = Math.max(0, bounds.top + bounds.height - rect.bottom - gap - margin);
    const above = Math.max(0, rect.top - bounds.top - gap - margin);
    const goAbove = below < 200 && above > below;
    const height = Math.max(0, Math.min(320, goAbove ? above : below));
    const width = Math.max(0, Math.min(Math.max(rect.width, 180), bounds.width - margin * 2));
    const menu = state.menu;
    Object.assign(menu.style, { width: `${width}px`, maxHeight: `${height}px`, left: '0px', top: '0px' });
    const menuRect = menu.getBoundingClientRect();
    const left = Math.max(bounds.left + margin, Math.min(rect.left, bounds.left + bounds.width - width - margin));
    const top = goAbove ? rect.top - gap - menuRect.height : rect.bottom + gap;
    // A fallback portal can live inside a transformed dialog; account for its
    // fixed-position containing block rather than breaking modal ownership.
    const originX = state.popover ? 0 : menuRect.left;
    const originY = state.popover ? 0 : menuRect.top;
    menu.style.left = `${left - originX}px`;
    menu.style.top = `${Math.max(bounds.top + margin, top) - originY}px`;
    menu.dataset.placement = goAbove ? 'top' : 'bottom';
  }

  function open(state, edge) {
    sync(state, true);
    if (!states.has(state) || state.trigger.disabled || state.wrapper.hidden) return;
    if (opened && opened !== state) close(opened);
    if (opened === state) return;
    opened = state;
    state.typeahead = '';
    state.menu.hidden = false;
    state.popover = false;
    if (typeof state.menu.showPopover === 'function') {
      state.menu.setAttribute('popover', 'manual');
      try {
        state.menu.showPopover();
        state.popover = true;
      } catch {
        state.menu.removeAttribute('popover');
      }
    }
    if (!state.popover) {
      state.menu.removeAttribute('popover');
      fallbackParent(state).append(state.menu);
    }
    attr(state.trigger, 'aria-expanded', 'true');
    state.trigger.focus({ preventScroll: true });
    place(state);
    const items = enabled(state);
    setActive(state, edge === 'last' ? items.at(-1)?.index : edge === 'first' ? items[0]?.index : initialActive(state));
  }

  function close(state) {
    if (opened !== state) return;
    opened = null;
    if (state.popover) {
      try { state.menu.hidePopover(); } catch { /* An outer dialog may already have closed. */ }
    }
    state.menu.hidden = true;
    state.popover = false;
    if (state.menu.parentElement !== state.wrapper) state.wrapper.insertBefore(state.menu, state.error);
    attr(state.trigger, 'aria-expanded', 'false');
    attr(state.trigger, 'aria-activedescendant', null);
    state.items.forEach((item) => attr(item.node, 'data-active', null));
    state.typeahead = '';
  }

  function commit(state, index = state.active) {
    const item = state.items.find((entry) => entry.index === index && !entry.disabled);
    if (!item || state.select.matches(':disabled')) {
      close(state);
      return;
    }
    const changed = state.select.selectedIndex !== index;
    // Never edit selected attributes: Shopify's variant handler owns them.
    state.select.selectedIndex = index;
    close(state);
    sync(state, true);
    if (changed) {
      state.select.dispatchEvent(new Event('input', { bubbles: true }));
      state.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function move(state, delta) {
    const items = enabled(state);
    const current = items.findIndex((item) => item.index === state.active);
    setActive(state, items[Math.max(0, Math.min(items.length - 1, current + delta))]?.index);
  }

  function typeahead(state, key) {
    const now = Date.now();
    if (now - state.lastTyped > 700) state.typeahead = '';
    state.lastTyped = now;
    state.typeahead += key.toLocaleLowerCase();
    const repeated = [...state.typeahead].every((character) => character === state.typeahead[0]);
    const query = repeated ? state.typeahead[0] : state.typeahead;
    const items = enabled(state);
    const current = items.findIndex((item) => item.index === state.active);
    const start = repeated ? current + 1 : Math.max(0, current);
    for (let offset = 0; offset < items.length; offset += 1) {
      const item = items[(start + offset) % items.length];
      if (item.label.trim().toLocaleLowerCase().startsWith(query)) {
        setActive(state, item.index);
        break;
      }
    }
  }

  function onKeyDown(state, event) {
    if (event.isComposing || event.ctrlKey || event.metaKey || state.trigger.disabled) return;
    const key = event.key;
    const isOpen = opened === state;
    if (key === 'Escape' && isOpen) {
      event.preventDefault();
      event.stopPropagation();
      close(state);
      escapeKeyUp = true;
      clearTimeout(escapeTimer);
      escapeTimer = setTimeout(() => { escapeKeyUp = false; }, 1000);
      return;
    }
    if (key === 'Tab') {
      if (isOpen) commit(state);
      return;
    }
    if (event.altKey && key === 'ArrowUp' && isOpen) {
      event.preventDefault();
      commit(state);
      return;
    }
    if (event.altKey && key !== 'ArrowDown') return;
    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp', 'Enter', ' '].includes(key)) {
      event.preventDefault();
      if (!isOpen) {
        open(state, key === 'Home' || key === 'ArrowUp' ? 'first' : key === 'End' ? 'last' : undefined);
      } else if (key === 'Enter' || key === ' ') {
        commit(state);
      } else if (key === 'Home' || key === 'End') {
        const items = enabled(state);
        setActive(state, key === 'Home' ? items[0]?.index : items.at(-1)?.index);
      } else {
        move(state, key === 'PageDown' ? 10 : key === 'PageUp' ? -10 : key === 'ArrowDown' ? 1 : -1);
      }
      return;
    }
    if (key.length === 1 && !event.altKey) {
      event.preventDefault();
      if (!isOpen) open(state);
      if (opened === state) typeahead(state, key);
    }
  }

  function enhance(select) {
    if (!eligible(select) || instances.has(select) || !select.isConnected) return;
    let id;
    do { id = `fm-dropdown-${++serial}`; } while (document.getElementById(`${id}-trigger`));
    const wrapper = element('div', 'fm-dropdown');
    const trigger = element('button', 'fm-dropdown__trigger');
    const value = element('span', 'fm-dropdown__value');
    const arrow = element('span', 'fm-dropdown__arrow');
    const menu = element('div', 'fm-dropdown__menu');
    const error = element('span', 'fm-dropdown__error');
    trigger.id = `${id}-trigger`;
    trigger.type = 'button';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', `${id}-menu`);
    arrow.setAttribute('aria-hidden', 'true');
    menu.id = `${id}-menu`;
    menu.hidden = true;
    menu.setAttribute('role', 'listbox');
    error.id = `${id}-error`;
    error.hidden = true;
    error.setAttribute('aria-live', 'polite');
    trigger.append(value, arrow);
    wrapper.append(trigger, menu, error);
    const state = {
      id, select, wrapper, trigger, value, menu, error, listeners: [], items: [],
      signature: null, active: -1, host: null, invalid: false, popover: false,
      typeahead: '', lastTyped: 0,
      original: {
        tabindex: select.getAttribute('tabindex'),
        ariaHidden: select.getAttribute('aria-hidden'),
        nativeClass: select.classList.contains('fm-dropdown__native'),
      },
    };
    instances.set(select, state);
    states.add(state);
    try {
      select.after(wrapper);
      sync(state);
      on(state, trigger, 'click', () => opened === state ? close(state) : open(state));
      on(state, trigger, 'keydown', (event) => onKeyDown(state, event));
      on(state, menu, 'pointerdown', (event) => {
        // Keep DOM focus on the combobox, including in a drawer's focus trap.
        if (event.target.closest('.fm-dropdown__option')) event.preventDefault();
      });
      on(state, menu, 'pointermove', (event) => {
        const option = event.target.closest('.fm-dropdown__option');
        if (option && option.getAttribute('aria-disabled') !== 'true') setActive(state, Number(option.dataset.index), false);
      });
      on(state, menu, 'click', (event) => {
        const option = event.target.closest('.fm-dropdown__option');
        if (!option || option.getAttribute('aria-disabled') === 'true') return;
        event.preventDefault();
        commit(state, Number(option.dataset.index));
      });
      on(state, select, 'focus', () => state.trigger.focus({ preventScroll: true }));
      on(state, select, 'input', () => sync(state, true));
      on(state, select, 'change', () => sync(state, true));
      on(state, select, 'invalid', (event) => {
        event.preventDefault();
        state.invalid = true;
        // A script can set .value without an event before submitting a form.
        // Validation must show that current native value, not a stale label.
        sync(state, true);
        if (!invalidFocusClaimed) {
          invalidFocusClaimed = true;
          state.trigger.focus();
          queueMicrotask(() => { invalidFocusClaimed = false; });
        }
      });
      // Native fallback is hidden only after every handler has initialized.
      select.classList.add('fm-dropdown__native');
      select.setAttribute('tabindex', '-1');
      select.setAttribute('aria-hidden', 'true');
      wrapper.dataset.fmDropdownReady = 'true';
      // Product-info can restore focus synchronously before the observer sees
      // its replacement select. Transfer that already-held focus as well.
      if (document.activeElement === select) trigger.focus({ preventScroll: true });
    } catch {
      destroy(state);
    }
  }

  function destroy(state) {
    close(state);
    state.listeners.forEach((remove) => remove());
    state.menu.remove();
    state.wrapper.remove();
    if (!state.original.nativeClass) state.select.classList.remove('fm-dropdown__native');
    attr(state.select, 'tabindex', state.original.tabindex);
    attr(state.select, 'aria-hidden', state.original.ariaHidden);
    releaseHost(state);
    instances.delete(state.select);
    states.delete(state);
  }

  function refresh(root = document) {
    Array.from(states).forEach((state) => {
      if (!state.select.isConnected || !eligible(state.select)) destroy(state);
      else if (root === document || root === state.select || root.contains?.(state.select)) sync(state, true);
    });
    if (root instanceof HTMLSelectElement) enhance(root);
    root.querySelectorAll?.('select').forEach(enhance);
  }

  function queue(select) {
    if (select instanceof HTMLSelectElement) pending.add(select);
    if (flushQueued) return;
    flushQueued = true;
    queueMicrotask(() => {
      flushQueued = false;
      Array.from(states).forEach((state) => {
        if (!state.select.isConnected) destroy(state);
        else if (!state.wrapper.isConnected) pending.add(state.select);
      });
      const work = Array.from(pending);
      pending.clear();
      work.forEach((candidate) => {
        const state = instances.get(candidate);
        if (state) sync(state);
        else enhance(candidate);
      });
    });
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
      if (!target || target.closest('.fm-dropdown')) return;
      const select = target.closest('select');
      if (select) queue(select);
      if (mutation.type === 'attributes') {
        if (target.tagName === 'FIELDSET') target.querySelectorAll('select').forEach(queue);
        return;
      }
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE || node.classList.contains('fm-dropdown')) return;
        if (node.tagName === 'SELECT') queue(node);
        node.querySelectorAll('select').forEach(queue);
      });
      if (mutation.removedNodes.length) queue();
    });
  });

  document.addEventListener('pointerdown', (event) => {
    if (opened && !opened.wrapper.contains(event.target) && !opened.menu.contains(event.target)) close(opened);
  }, true);
  document.addEventListener('focusin', (event) => {
    if (opened && event.target !== opened.select && !opened.wrapper.contains(event.target) && !opened.menu.contains(event.target)) {
      commit(opened);
    }
  });
  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape' && escapeKeyUp) {
      escapeKeyUp = false;
      clearTimeout(escapeTimer);
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const label = target?.closest('label');
    if (!label || target.closest('button, a, input, select, textarea, .fm-dropdown')) return;
    const state = instances.get(label.control);
    if (!state) return;
    event.preventDefault();
    state.trigger.focus();
  });
  document.addEventListener('reset', (event) => {
    setTimeout(() => {
      states.forEach((state) => {
        if (state.select.form !== event.target) return;
        state.invalid = false;
        close(state);
        sync(state, true);
      });
    }, 0);
  }, true);
  document.addEventListener('scroll', (event) => {
    if (opened && event.target !== opened.menu && !opened.menu.contains(event.target)) close(opened);
  }, true);
  const closeOnViewportChange = () => { if (opened) close(opened); };
  window.addEventListener('resize', closeOnViewportChange);
  window.addEventListener('pageshow', () => refresh());
  window.visualViewport?.addEventListener('resize', closeOnViewportChange);
  window.visualViewport?.addEventListener('scroll', closeOnViewportChange);
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', () => { closeOnViewportChange(); queue(); });

  window.FMPureDropdowns = Object.freeze({ refresh });
  function start() {
    refresh();
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'multiple', 'size', 'required', 'label', 'selected', 'value', 'id',
        'aria-label', 'aria-labelledby', 'aria-describedby', 'aria-invalid', 'title', 'form', 'hidden'],
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
